/**
 * «OVERGANGER» — byråbyttene i registeret, måned for måned (Atle, 28. september).
 *
 * Radene skrives av byråskanningen (`scan`) og av byråer som selv er i Tvitvi (`agency_self`).
 * Ingen rute skriver dem; fanen leser. Se `booking/agency-scan.ts` for hva som er en endring.
 *
 * MÅNEDEN ER `ÅÅÅÅ-MM` OG ALDRI EN DATO. Vi vet når vi SÅ byttet, ikke når kontrakten ble signert,
 * og en dato ville påstått en presisjon ingen har.
 */
import { z } from 'zod';

import {
  AGENCY_MOVE_KINDS,
  AGENCY_MOVE_SOURCES,
  AGENCY_REVIEW_KINDS,
  MONTH_PATTERN,
} from '../booking/agency-scan.js';
import { cursorQuerySchema, page } from '../http/pagination.js';

export const agencyMoveKindSchema = z.enum(AGENCY_MOVE_KINDS).meta({ id: 'AgencyMoveKind' });
export const agencyMoveSourceSchema = z.enum(AGENCY_MOVE_SOURCES).meta({ id: 'AgencyMoveSource' });

const monthSchema = z.string().regex(MONTH_PATTERN);

export const agencyMoveDtoSchema = z
  .object({
    id: z.uuid(),
    /** `ÅÅÅÅ-MM`. */
    month: monthSchema,
    kind: agencyMoveKindSchema,
    source: agencyMoveSourceSchema,
    /** Null når byrået selv meldte en artist huset ikke har i registeret. Da er navnet alt. */
    artistId: z.uuid().nullable(),
    artistName: z.string(),
    artistImageUrl: z.string().nullable(),
    /** «Ny – ikke priset»: lagt til av skanningen, og ingen har satt en pris ennå. */
    artistNewUnpriced: z.boolean(),
    shortlisted: z.boolean(),
    fromAgencyId: z.uuid().nullable(),
    fromAgencyName: z.string().nullable(),
    toAgencyId: z.uuid().nullable(),
    toAgencyName: z.string().nullable(),
    genre: z.string().nullable(),
    /** Hjemstedet, som Greenroom kaller base. */
    base: z.string().nullable(),
    /** Siden vi så det på. Null for byråets egne endringer, som ikke har en offentlig side. */
    evidenceUrl: z.string().nullable(),
    scanName: z.string().nullable(),
  })
  .meta({ id: 'AgencyMoveDto' });
export type AgencyMoveDto = z.infer<typeof agencyMoveDtoSchema>;

export const AGENCY_MOVE_SORT_KEYS = [
  'month',
  'artist',
  'kind',
  'fromAgency',
  'toAgency',
  'genre',
  'base',
  'source',
] as const;
export type AgencyMoveSortKey = (typeof AGENCY_MOVE_SORT_KEYS)[number];

const splitList = (value: unknown): unknown => {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value;
  const text = String(value).trim();
  return text.length === 0 ? undefined : text.split(',');
};

const MAX_FILTER_VALUES = 20;

const flag = z
  .preprocess((value) => (value === 'true' || value === true ? true : undefined), z.boolean())
  .optional();

export const listAgencyMovesQuerySchema = cursorQuerySchema(AGENCY_MOVE_SORT_KEYS)
  .extend({
    /** Artist eller byrå, fra eller til. */
    q: z.string().trim().min(1).max(120).optional(),
    kind: z
      .preprocess(splitList, z.array(agencyMoveKindSchema).min(1).max(AGENCY_MOVE_KINDS.length))
      .optional(),
    /** Byrået, FRA eller TIL — «hva skjedde med Polar Artist» er begge veier. */
    agencyId: z.preprocess(splitList, z.array(z.uuid()).min(1).max(MAX_FILTER_VALUES)).optional(),
    fromMonth: monthSchema.optional(),
    toMonth: monthSchema.optional(),
    genre: z
      .preprocess(
        splitList,
        z.array(z.string().trim().min(1).max(60)).min(1).max(MAX_FILTER_VALUES),
      )
      .optional(),
    favourites: flag,
  })
  .meta({ id: 'ListAgencyMovesQuery' });
export type ListAgencyMovesQuery = z.infer<typeof listAgencyMovesQuerySchema>;

/**
 * SIDENE OG MÅNEDSTALLENE KOMMER FRA SERVEREN (QA13 26). Tallet ved hver måned talte de lastede
 * radene, og med sider ville det vært «så mange på denne sida». `monthTotals` er en gruppert
 * telling over hele filteret (en sum per måned, ikke en COUNT(*) for sidedeling), og `total` er
 * summen av den — det sidevelgeren trenger for sidetallene.
 */
export const agencyMoveListResponseSchema = page(agencyMoveDtoSchema, 'AgencyMoveListResponse')
  .extend({
    total: z.number().int().nonnegative(),
    monthTotals: z.array(
      z.object({ month: monthSchema, count: z.number().int().nonnegative() }),
    ),
  })
  .meta({ id: 'AgencyMoveListResponse' });
export type AgencyMoveListResponse = z.infer<typeof agencyMoveListResponseSchema>;

/**
 * Grafen: de siste tolv månedene, stablet. Samme filtre som lista unntatt perioden — grafen ER
 * perioden, og et klikk på en måned er det som snevrer lista inn.
 */
export const agencyMoveMonthsQuerySchema = listAgencyMovesQuerySchema
  .pick({ q: true, kind: true, agencyId: true, genre: true, favourites: true })
  .extend({
    /** Siste måned i grafen. Utelatt: inneværende måned i Oslo. */
    until: monthSchema.optional(),
  })
  .meta({ id: 'AgencyMoveMonthsQuery' });
export type AgencyMoveMonthsQuery = z.infer<typeof agencyMoveMonthsQuerySchema>;

export const agencyMoveMonthSchema = z
  .object({
    month: monthSchema,
    in: z.number().int().nonnegative(),
    out: z.number().int().nonnegative(),
    switched: z.number().int().nonnegative(),
  })
  .meta({ id: 'AgencyMoveMonth' });
export type AgencyMoveMonth = z.infer<typeof agencyMoveMonthSchema>;

export const agencyMoveMonthsResponseSchema = z
  .object({
    /** Alltid tolv, eldst først — også de tomme. En graf med hull er en graf som lyver om tid. */
    months: z.array(agencyMoveMonthSchema),
    /** Siste skanning, for «Sist skannet …». Null før noen skanning finnes. */
    lastScannedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'AgencyMoveMonthsResponse' });
export type AgencyMoveMonthsResponse = z.infer<typeof agencyMoveMonthsResponseSchema>;

export const agencyReviewKindSchema = z.enum(AGENCY_REVIEW_KINDS).meta({ id: 'AgencyReviewKind' });

/** «Må sjekkes» fra siste skanning: det skanningen ikke turte å avgjøre selv. */
export const agencyReviewItemDtoSchema = z
  .object({
    id: z.uuid(),
    kind: agencyReviewKindSchema,
    agencyName: z.string().nullable(),
    artistName: z.string().nullable(),
    candidates: z.array(z.object({ id: z.uuid(), name: z.string() })),
    evidenceUrl: z.string().nullable(),
    /** Forrige og nåværende antall for `count_drop`. */
    previousCount: z.number().int().nonnegative().nullable(),
    currentCount: z.number().int().nonnegative().nullable(),
  })
  .meta({ id: 'AgencyReviewItemDto' });
export type AgencyReviewItemDto = z.infer<typeof agencyReviewItemDtoSchema>;

export const agencyReviewListResponseSchema = z
  .object({
    items: z.array(agencyReviewItemDtoSchema),
    scanName: z.string().nullable(),
  })
  .meta({ id: 'AgencyReviewListResponse' });
export type AgencyReviewListResponse = z.infer<typeof agencyReviewListResponseSchema>;

/**
 * BOOKINGKONTAKTEN på artisten (del C): agenten, eller byråets generelle kontakt når ingen agent
 * er kjent. Bare forretningskontakt fra offentlige sider, og aldri lagt i husets Kontakter.
 */
export const bookingContactSchema = z
  .object({
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    /** «Generell kontakt – [byrå]»: byråets post@ og sentralbord, ikke en person. */
    general: z.boolean(),
    agencyName: z.string().nullable(),
  })
  .meta({ id: 'BookingContact' });
export type BookingContact = z.infer<typeof bookingContactSchema>;
